// PrintReports.js

export const printCreditDebitReport = ({
  records = [],
  from,
  to,
  totalCredit = 0,
  totalDebit = 0,
  session = "",
  type = "All",
}) => {
  const win = window.open("", "_blank", "width=1200,height=800");

  if (!win) {
    alert("Please allow pop-ups to print the report.");
    return;
  }

  /*
  |--------------------------------------------------------------------------
  | HELPERS
  |--------------------------------------------------------------------------
  */

  const escapeHtml = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return escapeHtml(date);
    }

    return parsedDate.toLocaleDateString("en-IN");
  };

  const getTransactionType = (item) => {
    if (item?.type === "fee") {
      return "Credit";
    }

    if (item?.type === "expense") {
      return "Debit";
    }

    if (item?.transaction_type) {
      return (
        item.transaction_type.charAt(0).toUpperCase() +
        item.transaction_type.slice(1)
      );
    }

    return "-";
  };

  const getModuleLabel = (item) => {
    if (item?.module_label) {
      return item.module_label;
    }

    if (Array.isArray(item?.module_names) && item.module_names.length) {
      return item.module_names.join(", ");
    }

    if (Array.isArray(item?.module)) {
      const modules = item.module.map((module) => module?.type).filter(Boolean);

      if (modules.length) {
        return modules.join(", ");
      }
    }

    return item?.type === "expense" ? "Expense" : "Fee Payment";
  };

  const getSourceLabel = (item) => {
    if (item?.source_label) {
      return item.source_label;
    }

    if (item?.type === "expense") {
      return "Expense";
    }

    return "Student";
  };

  const getPersonName = (item) => {
    const person = item?.person || {};

    return (
      person?.name ||
      item?.student?.name ||
      item?.hostel_student?.name ||
      item?.library_student?.name ||
      item?.bus_student?.name ||
      item?.staff?.name ||
      "-"
    );
  };

  const getRegistrationNo = (item) => {
    const person = item?.person || {};

    return person?.registration_no || item?.student?.registration_no || "";
  };

  const getReference = (item) => {
    const references = [];

    if (item?.receipt_no) {
      references.push(item.receipt_no);
    }

    if (item?.transaction_no) {
      references.push(item.transaction_no);
    }

    return references.length ? references.join(" • ") : "-";
  };

  const getBankAccount = (item) => {
    return item?.bank?.account_no || item?.account_no || "-";
  };

  const getBankName = (item) => {
    return item?.bank?.owner_name || item?.bank?.name || "-";
  };

  /*
  |--------------------------------------------------------------------------
  | TOTALS
  |--------------------------------------------------------------------------
  */

  const credit = Number(totalCredit || 0);
  const debit = Number(totalDebit || 0);
  const balance = credit - debit;

  /*
  |--------------------------------------------------------------------------
  | TABLE ROWS
  |--------------------------------------------------------------------------
  */

  const rows = records
    .map((item, index) => {
      const isCredit = item?.type === "fee";

      const isDebit = item?.type === "expense";

      const amount = Number(item?.amount || 0);

      const typeLabel = getTransactionType(item);

      const typeClass = isCredit ? "credit" : isDebit ? "debit" : "";

      const moduleLabel = getModuleLabel(item);

      const sourceLabel = getSourceLabel(item);

      const personName = getPersonName(item);

      const registrationNo = getRegistrationNo(item);

      const reference = getReference(item);

      const bankAccount = getBankAccount(item);

      const bankName = getBankName(item);

      return `
        <tr>

          <td class="center">
            ${index + 1}
          </td>

          <td class="nowrap">
            ${formatDate(item?.date || item?.created_at)}
          </td>

          <td class="center">
            <span class="type ${typeClass}">
              ${escapeHtml(typeLabel)}
            </span>
          </td>

          <td>

            <div class="primary">
              ${escapeHtml(moduleLabel)}
            </div>

            <div class="secondary">
              ${escapeHtml(sourceLabel)}
            </div>

          </td>

          <td>

            <div class="primary">
              ${escapeHtml(personName)}
            </div>

            ${
              registrationNo
                ? `
                  <div class="secondary">
                    ${escapeHtml(registrationNo)}
                  </div>
                `
                : ""
            }

          </td>

          <td>

            <div class="primary">
              ${escapeHtml(reference)}
            </div>

            ${
              item?.payment_method
                ? `
                  <div class="secondary">
                    ${escapeHtml(item.payment_method)}
                  </div>
                `
                : ""
            }

          </td>

          <td>

            <div class="primary">
              ${escapeHtml(bankAccount)}
            </div>

            <div class="secondary">
              ${escapeHtml(bankName)}
            </div>

          </td>

          <td>
            ${escapeHtml(item?.note || "-")}
          </td>

          <td
            class="amount ${isCredit ? "credit-amount" : "debit-amount"}"
          >
            ${isCredit ? "+" : "-"}₹${formatAmount(amount)}
          </td>

        </tr>
      `;
    })
    .join("");

  /*
  |--------------------------------------------------------------------------
  | WRITE PRINT DOCUMENT
  |--------------------------------------------------------------------------
  */

  win.document.write(`
<!DOCTYPE html>

<html>

<head>

  <meta charset="UTF-8">

  <title>
    Credit Debit Ledger
  </title>

  <style>

    @page {
      size: A4 landscape;
      margin: 10mm;
    }

    * {
      box-sizing: border-box;
    }

    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      color: #000;
      margin: 0;
      padding: 0;
    }


    /* ==========================================================
       HEADER
    ========================================================== */

    .report-header {
      text-align: center;
      margin-bottom: 14px;
    }

    .report-header h2 {
      margin: 0 0 5px;
      font-size: 20px;
    }

    .report-header .date {
      font-size: 12px;
      color: #555;
    }

    .session {
      margin-top: 4px;
      font-size: 11px;
      color: #555;
    }


    /* ==========================================================
       SUMMARY
    ========================================================== */

    .summary {
      display: grid;
      grid-template-columns:
        repeat(4, 1fr);

      gap: 8px;

      margin-bottom: 15px;
    }

    .summary-box {
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 8px 10px;
    }

    .summary-label {
      font-size: 10px;
      color: #666;
      margin-bottom: 3px;
    }

    .summary-value {
      font-size: 15px;
      font-weight: bold;
    }

    .credit-value {
      color: #198754;
    }

    .debit-value {
      color: #dc3545;
    }

    .balance-positive {
      color: #198754;
    }

    .balance-negative {
      color: #dc3545;
    }


    /* ==========================================================
       TABLE
    ========================================================== */

    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }

    thead {
      display: table-header-group;
    }

    tr {
      page-break-inside: avoid;
    }

    th,
    td {
      border: 1px solid #bbb;
      padding: 6px;
      vertical-align: top;
    }

    th {
      background: #f2f2f2;
      font-size: 10px;
      text-transform: uppercase;
    }

    td {
      font-size: 10px;
    }


    /* ==========================================================
       COLUMN WIDTHS
    ========================================================== */

    th:nth-child(1),
    td:nth-child(1) {
      width: 4%;
    }

    th:nth-child(2),
    td:nth-child(2) {
      width: 8%;
    }

    th:nth-child(3),
    td:nth-child(3) {
      width: 8%;
    }

    th:nth-child(4),
    td:nth-child(4) {
      width: 14%;
    }

    th:nth-child(5),
    td:nth-child(5) {
      width: 14%;
    }

    th:nth-child(6),
    td:nth-child(6) {
      width: 14%;
    }

    th:nth-child(7),
    td:nth-child(7) {
      width: 12%;
    }

    th:nth-child(8),
    td:nth-child(8) {
      width: 14%;
    }

    th:nth-child(9),
    td:nth-child(9) {
      width: 12%;
    }


    /* ==========================================================
       CELL HELPERS
    ========================================================== */

    .center {
      text-align: center;
    }

    .nowrap {
      white-space: nowrap;
    }

    .primary {
      font-weight: 600;
      line-height: 1.35;
    }

    .secondary {
      color: #666;
      font-size: 9px;
      margin-top: 2px;
      line-height: 1.3;
    }


    /* ==========================================================
       TYPE BADGES
    ========================================================== */

    .type {
      display: inline-block;
      padding: 3px 7px;
      border-radius: 12px;
      font-size: 9px;
      font-weight: bold;
    }

    .type.credit {
      color: #198754;
      background: #e8f5e9;
    }

    .type.debit {
      color: #dc3545;
      background: #fdecec;
    }


    /* ==========================================================
       AMOUNT
    ========================================================== */

    .amount {
      text-align: right;
      white-space: nowrap;
      font-weight: bold;
      font-size: 11px;
    }

    .credit-amount {
      color: #198754;
    }

    .debit-amount {
      color: #dc3545;
    }


    /* ==========================================================
       FOOTER
    ========================================================== */

    .footer {
      margin-top: 12px;
      text-align: right;
      font-size: 9px;
      color: #777;
    }


    /* ==========================================================
       PRINT
    ========================================================== */

    @media print {

      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .no-print {
        display: none !important;
      }

    }

  </style>

</head>


<body>


  <!-- ==========================================================
       HEADER
  ========================================================== -->

  <div class="report-header">

    <h2>
      Credit / Debit Ledger
    </h2>

    <div class="date">
      ${escapeHtml(from || "-")}
      &nbsp; to &nbsp;
      ${escapeHtml(to || "-")}
    </div>

    ${
      session
        ? `
          <div class="session">
            Session: ${escapeHtml(session)}
          </div>
        `
        : ""
    }

    ${
      type && type !== "All"
        ? `
          <div class="session">
            Transaction Type:
            ${escapeHtml(type)}
          </div>
        `
        : ""
    }

  </div>


  <!-- ==========================================================
       SUMMARY
  ========================================================== -->

  <div class="summary">

    <div class="summary-box">

      <div class="summary-label">
        Total Credit
      </div>

      <div class="summary-value credit-value">
        ₹${formatAmount(credit)}
      </div>

    </div>


    <div class="summary-box">

      <div class="summary-label">
        Total Debit
      </div>

      <div class="summary-value debit-value">
        ₹${formatAmount(debit)}
      </div>

    </div>


    <div class="summary-box">

      <div class="summary-label">
        Net Balance
      </div>

      <div
        class="summary-value ${
          balance >= 0 ? "balance-positive" : "balance-negative"
        }"
      >
        ₹${formatAmount(balance)}
      </div>

    </div>


    <div class="summary-box">

      <div class="summary-label">
        Total Transactions
      </div>

      <div class="summary-value">
        ${records.length}
      </div>

    </div>

  </div>


  <!-- ==========================================================
       TABLE
  ========================================================== -->

  <table>

    <thead>

      <tr>

        <th>#</th>

        <th>Date</th>

        <th>Type</th>

        <th>Transaction</th>

        <th>Party</th>

        <th>Reference</th>

        <th>Bank</th>

        <th>Remark</th>

        <th>Amount</th>

      </tr>

    </thead>


    <tbody>

      ${
        rows ||
        `
          <tr>
            <td
              colspan="9"
              style="text-align:center;padding:20px;"
            >
              No transactions found.
            </td>
          </tr>
        `
      }

    </tbody>

  </table>


  <div class="footer">
    Generated on ${new Date().toLocaleString("en-IN")}
  </div>


</body>

</html>
  `);

  win.document.close();

  /*
  |--------------------------------------------------------------------------
  | PRINT
  |--------------------------------------------------------------------------
  */

  win.onload = () => {
    setTimeout(() => {
      win.focus();

      win.print();

      win.close();
    }, 500);
  };
};
